/**
 * useActrData.ts - React hooks for Actr data operations
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { useState, useEffect, useCallback } from 'react';
import { useActr } from '../ActrContext';

/**
 * Hook to query entities with automatic updates
 * @param {string} entityType - Type of entity
 * @param {Object} filters - Query filters
 * @returns {Object} { data, loading, error, refetch }
 */
export function useActrQuery(entityType, filters = {}) {
  const actr = useActr();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const results = await actr.query(entityType, filters);
      setData(results);
    } catch (err) {
      setError(err);
      console.error(`[useActrQuery] Error querying ${entityType}:`, err);
    } finally {
      setLoading(false);
    }
  }, [actr, entityType, JSON.stringify(filters)]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
}

/**
 * Hook to read a single entity
 * @param {string} entityType - Type of entity
 * @param {string} id - Entity ID
 * @returns {Object} { data, loading, error, refetch }
 */
export function useActrRead(entityType, id) {
  const actr = useActr();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!id) {
      setData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await actr.read(entityType, id);
      setData(result);
    } catch (err) {
      setError(err);
      console.error(`[useActrRead] Error reading ${entityType}:${id}:`, err);
    } finally {
      setLoading(false);
    }
  }, [actr, entityType, id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
}

/**
 * Hook for CRUD mutations
 * @param {string} entityType - Type of entity
 * @returns {Object} Mutation functions
 */
export function useActrMutation(entityType) {
  const actr = useActr();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const create = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const result = await actr.create(entityType, data);
      return result;
    } catch (err) {
      setError(err);
      console.error(`[useActrMutation] Create error:`, err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [actr, entityType]);

  const update = useCallback(async (id, updates) => {
    try {
      setLoading(true);
      setError(null);
      const result = await actr.update(entityType, id, updates);
      return result;
    } catch (err) {
      setError(err);
      console.error(`[useActrMutation] Update error:`, err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [actr, entityType]);

  const remove = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const result = await actr.delete(entityType, id);
      return result;
    } catch (err) {
      setError(err);
      console.error(`[useActrMutation] Delete error:`, err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [actr, entityType]);

  return {
    create,
    update,
    remove,
    loading,
    error
  };
}

/**
 * Hook for relationship operations
 * @param {string} entityType - Type of entity
 * @param {string} id - Entity ID
 * @returns {Object} Relationship functions
 */
export function useActrRelations(entityType, id) {
  const actr = useActr();

  const relate = useCallback(async (relationType, targetId) => {
    return await actr.relate(entityType, id, relationType, targetId);
  }, [actr, entityType, id]);

  const unrelate = useCallback(async (relationType, targetId) => {
    return await actr.unrelate(entityType, id, relationType, targetId);
  }, [actr, entityType, id]);

  const getRelated = useCallback(async (relationType) => {
    return await actr.getRelated(entityType, id, relationType);
  }, [actr, entityType, id]);

  return {
    relate,
    unrelate,
    getRelated
  };
}
